import React, { useCallback, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import DeleteIcon from '@material-ui/icons/Delete';
import VppDatePicker from '../../../../components/datePicker/vppDatePicker';
import VppSelect from '../../../../components/select/VppSelect';
import US_STATES_WITH_INITIALS from '../../../../us_states_and_initials';
import moment from 'moment-timezone';
import { useDispatch, useSelector } from 'react-redux';
import { getRules } from '../../../../actions/rules.actions';
import PermitCardActions from './PermitCardActions';
import PermitCardView from './PermitCardView';
import {
  mergeVehicleFields,
  normalizeLicenceState,
} from './licenceStateUtils';
const licencePlateValidator = {
  required: 'License plate is required.',
  maxLength: {
    value: 11,
    message: 'License plate must be 11 or less characters',
  },
  pattern: {
    value: /^[A-Z0-9]*$/,
    message:
      'License plate must be only capital numbers and letters without especial chars',
  },
};

moment.tz.setDefault('America/Chicago');

const useStyles = makeStyles({
  fieldLabel: {
    display: 'block',
    marginBottom: 2,
    lineHeight: 1.2,
  },
  fieldControl: {
    marginTop: 0,
    '& .MuiFormControl-root': {
      marginTop: 0,
      marginBottom: 0,
    },
    '& .MuiTextField-root': {
      marginTop: 0,
      marginBottom: 0,
    },
  },
});

const PermitFieldLabel = ({ children, className, style }) => (
  <Typography
    variant="caption"
    color="textPrimary"
    className={className}
    display="block"
    style={style}
  >
    {children}
  </Typography>
);

const resolveVehicleAtIndex = (vehicles, index) => {
  if (!vehicles) {
    return {};
  }

  if (Array.isArray(vehicles)) {
    return vehicles[index] || {};
  }

  if (typeof vehicles === 'object') {
    return vehicles[index] ?? vehicles[String(index)] ?? {};
  }

  return {};
};

const getExpirationDate = (guest_hours_start, guest_hours_finish) => {
  if (!guest_hours_start || !guest_hours_finish) {
    console.log({ guest_hours_start, guest_hours_finish });
  }

  const currentTime = moment();
  let time = guest_hours_finish?.split(':');

  if (guest_hours_start === guest_hours_finish) {
    return currentTime.add(1, 'days').toDate();
  } else {
    const targetTime = moment().set({
      hours: time[0],
      minutes: time[1],
      seconds: time[2],
    });

    return currentTime.format('HH:mm') >= guest_hours_finish
      ? targetTime.add(1, 'days').toDate()
      : targetTime.toDate();
  }
}

const UnitsVehiclesBlock = ({
  fieldId = null,
  vehicleNumber,
  vehicleIndex,
  vehicleTitle,
  type,
  vehicle = null,
  control,
  register,
  fieldErrors,
  deleteButtonHandler,
  watch,
  getValues,
  formErrors,
  setValue,
  showActions,
  submitDisabled,
  isUnitLoaded = false,
  startInEditMode = false,
  onSave,
  onCancel,
}) => {
  const rules = useSelector((state) => state.rules);
  const dispatch = useDispatch();
  const classes = useStyles();
  const [isEditing, setIsEditing] = useState(startInEditMode);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const wasEditingRef = useRef(isEditing);
  const disabled = !isEditing;
  const { guest_hours_finish, guest_hours_start } = rules;
  const statusSelect = ['pending', 'active', 'blocked', 'suspended'].map(
    (i) => ({
      name: i,
      value: i,
    })
  );
  const inputNamePrefix =
    type === 'tenant' ? 'tenant_vehicles' : 'guest_vehicles';

  const inputName = (name) => `${inputNamePrefix}[${vehicleIndex}].${name}`;
  const today = new Date();

  const isEmptyVehicleSlot = (data) =>
    !data?.id &&
    !data?.licence_plate &&
    !data?.make &&
    !data?.model &&
    !data?.color &&
    !normalizeLicenceState(data?.licence_state);

  const getCurrentVehicleData = useCallback(() => {
    const baseVehicle =
      vehicle && typeof vehicle === 'object' ? vehicle : {};
    const vehiclesFromWatch = watch(inputNamePrefix);
    const vehiclesFromForm = getValues ? getValues(inputNamePrefix) : null;
    const formVehicle = resolveVehicleAtIndex(vehiclesFromForm, vehicleIndex);
    const watchedVehicle = resolveVehicleAtIndex(
      vehiclesFromWatch,
      vehicleIndex
    );

    // Empty padded slots must stay blank after another card saves and shifts
    // to the front. Ignore leftover form/watch values until the user is editing.
    if (isEmptyVehicleSlot(baseVehicle)) {
      if (!isEditing) {
        return {};
      }
      return mergeVehicleFields({}, formVehicle, watchedVehicle);
    }

    return mergeVehicleFields(baseVehicle, formVehicle, watchedVehicle);
  }, [
    getValues,
    inputNamePrefix,
    isEditing,
    vehicle,
    vehicleIndex,
    watch,
  ]);

  const populateEditFields = useCallback(
    (data) => {
      const fields = [
        'id',
        'status',
        'expiration_date',
        'licence_plate',
        'licence_state',
        'make',
        'model',
        'color',
        'issue_date',
      ];
      // Use the slot prop, not merged form data — form may still hold values
      // from before this permit shifted to index 0 after save.
      const emptySlot = isEmptyVehicleSlot(
        vehicle && typeof vehicle === 'object' ? vehicle : {}
      );

      fields.forEach((field) => {
        let value = data?.[field];

        if (field === 'licence_state') {
          value = normalizeLicenceState(value);
        }

        if (emptySlot) {
          if (field === 'issue_date') {
            setValue(inputName(field), new Date());
            return;
          }
          if (field === 'status') {
            setValue(inputName(field), 'pending');
            return;
          }
          if (field === 'expiration_date') {
            setValue(
              inputName(field),
              type === 'tenant'
                ? moment().add(1, 'years').endOf('day').toDate()
                : getExpirationDate(guest_hours_start, guest_hours_finish)
            );
            return;
          }
          setValue(inputName(field), '');
          return;
        }

        if (value !== undefined && value !== null && value !== '') {
          setValue(inputName(field), value);
        } else if (field === 'licence_state' || field === 'licence_plate') {
          setValue(inputName(field), '');
        }
      });

      if (!data?.issue_date || emptySlot) {
        setValue(inputName('issue_date'), new Date());
      }
    },
    [
      guest_hours_finish,
      guest_hours_start,
      setValue,
      type,
      vehicle,
      vehicleIndex,
    ]
  );

  const handleStartEdit = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleConfirmDelete = () => {
    setDeleteDialogOpen(false);
    // Prefer the slot prop id — form merge can be empty/stale on empty cards.
    const permitId = vehicle?.id || getCurrentVehicleData().id || null;
    deleteButtonHandler(vehicleIndex, type, permitId);
    // Empty / unsaved: leave edit mode so the card returns to view (EDIT button).
    if (!permitId) {
      setIsEditing(false);
    }
  };

  const errorCheck = (name) => {
    return (
      (fieldErrors[inputNamePrefix] &&
        fieldErrors[inputNamePrefix][vehicleIndex] &&
        fieldErrors[inputNamePrefix][vehicleIndex][name]) ||
      (formErrors[inputNamePrefix] &&
        formErrors[inputNamePrefix][vehicleIndex] &&
        formErrors[inputNamePrefix][vehicleIndex][name])
    );
  };

  const getGuestDate = useCallback(() => {
    return getExpirationDate(guest_hours_start, guest_hours_finish)
  }, [guest_hours_start, guest_hours_finish]);

  React.useEffect(() => {
    dispatch(getRules());
  }, [dispatch]);

  React.useEffect(() => {
    if (startInEditMode) {
      setIsEditing(true);
    } else {
      setIsEditing(false);
    }
  }, [startInEditMode]);

  React.useEffect(() => {
    if (isEditing && !wasEditingRef.current) {
      populateEditFields(getCurrentVehicleData());
    }

    wasEditingRef.current = isEditing;
  }, [getCurrentVehicleData, isEditing, populateEditFields]);

  const errorText = (name) => {
    if (!errorCheck(name)) {
      return null;
    }
    const fromFieldErrors =
      fieldErrors?.[inputNamePrefix]?.[vehicleIndex]?.[name];
    if (Array.isArray(fromFieldErrors)) {
      return fromFieldErrors.join(',');
    }
    if (typeof fromFieldErrors === 'string') {
      return fromFieldErrors;
    }
    return formErrors?.[inputNamePrefix]?.[vehicleIndex]?.[name]?.message || null;
  };

  const renderAutocompleteTextField = (params) => {
    return (
      <TextField
        {...params}
        name={inputName('licence_state')}
        type="text"
        variant="outlined"
        margin="none"
        error={errorCheck('licence_state')}
        helperText={errorText('licence_state')}
        fullWidth
      />
    );
  };

  const defaulteExpirationDateValue = type === 'tenant' ?
    moment().add(1, 'years').endOf('day').toDate() : getGuestDate()

  const viewValues = getCurrentVehicleData();
  const watchedLicenceState = watch(inputName('licence_state'));
  // Prefer live form value (including intentional clear) over stale view merge.
  const licenceStateCode = normalizeLicenceState(
    watchedLicenceState !== undefined && watchedLicenceState !== null
      ? watchedLicenceState
      : viewValues.licence_state
  );

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <Box
        px={2}
        pt={1.5}
        pb={0.5}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="subtitle2" color="primary">
          {vehicleTitle} {vehicleNumber}
        </Typography>
        <IconButton
          id={`delete_vehicle_button_${fieldId ?? 'unknown'}_${vehicleNumber}`}
          aria-label="delete"
          disabled={disabled}
          size="small"
          onClick={() => setDeleteDialogOpen(true)}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby={`delete_vehicle_dialog_title_${fieldId ?? 'unknown'}_${vehicleNumber}`}
      >
        <DialogTitle
          id={`delete_vehicle_dialog_title_${fieldId ?? 'unknown'}_${vehicleNumber}`}
        >
          Delete permit?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this {vehicleTitle.toLowerCase()}{' '}
            permit? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            id={`cancel_delete_vehicle_button_${fieldId ?? 'unknown'}_${vehicleNumber}`}
            onClick={() => setDeleteDialogOpen(false)}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            id={`confirm_delete_vehicle_button_${fieldId ?? 'unknown'}_${vehicleNumber}`}
            onClick={handleConfirmDelete}
            color="secondary"
            variant="contained"
            disabled={submitDisabled}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <input
        name={inputName('id')}
        type="hidden"
        ref={register}
        defaultValue={viewValues.id ?? ''}
      />
      <input
        name={inputName('issue_date')}
        type="hidden"
        ref={register}
        defaultValue={viewValues.issue_date || today}
      />
      <input
        name={inputName('licence_state')}
        type="hidden"
        ref={register}
        defaultValue={licenceStateCode}
      />
      {isEditing ? (
      <Box px={2} pb={1}>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <PermitFieldLabel className={classes.fieldLabel}>
              Status
            </PermitFieldLabel>
            <Box className={classes.fieldControl}>
              <VppSelect
                id={`vehicle_status_select_${fieldId ?? 'unknown'}_${vehicleNumber}`}
                name={inputName('status')}
                control={control}
                options={statusSelect}
                defaultValue={viewValues.status}
                disabled={disabled}
                fullWidth
              />
            </Box>
          </Grid>
          <Grid item xs={6}>
            <PermitFieldLabel className={classes.fieldLabel}>
              Expiration Date
            </PermitFieldLabel>
            <Box className={classes.fieldControl}>
              <VppDatePicker
                id={`vehicle_expiration_date_picker_${fieldId ?? 'unknown'}_${vehicleNumber}`}
                name={inputName('expiration_date')}
                size="small"
                control={control}
                disabled={disabled}
                error={errorCheck('expiration_date')}
                helperText={errorText('expiration_date')}
                defaultValue={
                  viewValues.expiration_date || defaulteExpirationDateValue
                }
                fullWidth
              />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Tooltip
              title="please enter letters and number only for your license plate example: ABC123"
              placement="top"
            >
              <PermitFieldLabel
                className={classes.fieldLabel}
                style={{ cursor: 'help' }}
              >
                License Plate
              </PermitFieldLabel>
            </Tooltip>
            <TextField
              className={classes.fieldControl}
              margin="none"
              {...register(inputName('licence_plate'), licencePlateValidator)}
              name={inputName('licence_plate')}
              id={`vehicle_licence_plate_textfield_${fieldId ?? 'unknown'}_${vehicleNumber}`}
              size="small"
              defaultValue={viewValues.licence_plate ?? ''}
              disabled={disabled}
              inputRef={register}
              onChange={(event) => {
                control.fieldsRef.current[
                  inputName('licence_plate')
                ].ref.value = event.target.value
                  .toUpperCase()
                  .replaceAll(/[\W]/g, '');
              }}
              autoComplete="off"
              error={errorCheck('licence_plate')}
              helperText={errorText('licence_plate')}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <PermitFieldLabel className={classes.fieldLabel}>
              Licence State
            </PermitFieldLabel>
            <Box className={classes.fieldControl}>
            <Autocomplete
              id={`vehicle_licence_state_autocomplete_${fieldId ?? 'unknown'}_${vehicleNumber}`}
              options={Object.keys(US_STATES_WITH_INITIALS)}
              getOptionLabel={(option) =>
                US_STATES_WITH_INITIALS[option] || option || ''
              }
              getOptionSelected={(option, value) => option === value}
              value={licenceStateCode || null}
              onChange={(event, newValue) => {
                setValue(
                  inputName('licence_state'),
                  normalizeLicenceState(newValue)
                );
              }}
              disabled={disabled}
              renderInput={renderAutocompleteTextField}
            />
            </Box>
          </Grid>
          <Grid item xs={6}>
            <PermitFieldLabel className={classes.fieldLabel}>Make</PermitFieldLabel>
            <TextField
              className={classes.fieldControl}
              margin="none"
              id={`vehicle_make_textfield_${fieldId ?? 'unknown'}_${vehicleNumber}`}
              name={inputName('make')}
              size="small"
              defaultValue={viewValues.make ?? ''}
              disabled={disabled}
              inputRef={register}
              error={errorCheck('make')}
              helperText={errorText('make')}
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <PermitFieldLabel className={classes.fieldLabel}>Model</PermitFieldLabel>
            <TextField
              className={classes.fieldControl}
              margin="none"
              id={`vehicle_model_textfield_${fieldId ?? 'unknown'}_${vehicleNumber}`}
              name={inputName('model')}
              size="small"
              defaultValue={viewValues.model ?? ''}
              disabled={disabled}
              inputRef={register}
              error={errorCheck('model')}
              helperText={errorText('model')}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <PermitFieldLabel className={classes.fieldLabel}>Color</PermitFieldLabel>
            <TextField
              className={classes.fieldControl}
              margin="none"
              id={`vehicle_color_textfield_${fieldId ?? 'unknown'}_${vehicleNumber}`}
              name={inputName('color')}
              size="small"
              defaultValue={viewValues.color ?? ''}
              disabled={disabled}
              inputRef={register}
              error={errorCheck('color')}
              helperText={errorText('color')}
              fullWidth
            />
          </Grid>
        </Grid>
      </Box>
      ) : (
        <PermitCardView
          key={`${type}-view-${vehicleIndex}-${isUnitLoaded ? 'loaded' : 'loading'}`}
          values={viewValues}
        />
      )}
      <Box mt="auto">
        <PermitCardActions
          isEditing={isEditing}
          onEdit={handleStartEdit}
          onCancel={() => {
            setIsEditing(false);
            onCancel();
          }}
          onSave={async () => {
            try {
              await onSave();
              setIsEditing(false);
            } catch {
              // Stay in edit mode when validation or save fails.
            }
          }}
          showActions={showActions}
          submitDisabled={submitDisabled}
          editButtonId={`${type}_permit_edit_button_${vehicleNumber}`}
          saveButtonId={`${type}_permit_save_button_${vehicleNumber}`}
          cancelButtonId={`${type}_permit_cancel_button_${vehicleNumber}`}
        />
      </Box>
    </Box>
  );
};

export default UnitsVehiclesBlock;
